<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { maxLength, minLength, required, requiredIf } from '@vuelidate/validators';
import { useApi } from '@/api';
import { useValidate } from '@/composables/useValidate';
import { useTypedStore } from '@/store';
import type {
  PostDomainsManagementDnsHostingAccountsData,
} from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
import type { OverrideFields } from '@/types/util';
//#endregion

//#region types
interface Props {
  dnsId?: string | null,
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface DnsHostingAccountParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type FormType = OverrideFields<PostDomainsManagementDnsHostingAccountsData['body'], {
  id?: string
}>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  dnsId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref({});

const form = ref<FormType>({
  name: '',
  auto_dns_management: true,
  dns_hosting_id: null,
  auth_data: {},
});
//#endregion

//#region computed
const dnsHostingProviders = computed(() => store.getters['misc/dnsHostingProviders']);

const isEdit = computed(() => {
  return props.dnsId;
});

const radioList = computed(() => {
  return [
    {
      label: t('ui.filters.select.auto'),
      value: true,
    },
    {
      label: t('ui.filters.select.manual'),
      value: false,
    },
  ];
});

const activeGroup = computed(() => {
  const isEmptyValues = Object.values(form.value.auth_data!).every((el) => !el);

  return authenticationArrayFields.value.map((child) => {
    if (isEmptyValues) return true;
    return child.some((el) => !!form.value.auth_data![el]);
  });
});

const authenticationArrayFields = computed(() => {
  if (form.value.dns_hosting_id) {
    const [dnsHostingProvider] = dnsHostingProviders.value.filter((provider) => {
      return provider.id === form.value.dns_hosting_id;
    });
    return dnsHostingProvider?.possible_auth_keys || [];
  }
  return [];
});

const pPopupTitle = computed(() => {
  return `${t(`domainManagement.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('domainManagement.popup.dnsHostingAccount')}`;
});

const rules = computed(() => {
  const authDataRules = authenticationArrayFields.value.reduce((acc, authenticationFields, i) => {
    authenticationFields.forEach((authenticationField) => {
      acc[authenticationField] = {
        required: requiredIf(() => form.value.auto_dns_management && !!activeGroup.value[i]),
      };
    });

    return acc;
  }, {} as Record<string, any>);

  return {
    name: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(255),
    },
    dns_hosting_id: {
      required: requiredIf(() => form.value.auto_dns_management),
    },
    auth_data: authDataRules,
  };
});
//#endregion

//#region methods
async function open() {
  if(isEdit.value) {
    const { data: { payload } } = await api.getDNSHostingAccountById(props.dnsId!);
    const {
      id,
      name,
      auto_dns_management,
      dns_hosting_id,
      auth_data,
    } = payload;

    form.value = {
      id,
      name,
      auto_dns_management,
      dns_hosting_id,
      auth_data,
    };
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};
  let promise;

  if(isEdit.value) {
    promise = api.updateDNSHostingAccount(form.value.id!, form.value);
  } else {
    promise = api.createDNSHostingAccount(form.value);
  }

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

function changeDnsManagement(value: boolean) {
  if (!value) {
    form.value.dns_hosting_id = null;
    changeDnsId();
  }
}

function changeDnsId() {
  form.value.auth_data = {};
}
//#endregion

//#region validate
const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules: rules,
  form: form,
});
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="dns-hosting-account-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :placeholder="$t(`domainManagement.popup.form.account_name.placeholder`)"
          :label="$t(`domainManagement.popup.form.account_name.label`)"
          autosize
          required
          size="big"
          :is-error="isInvalidField('name')"
          @blur="checkField('name')"
        />
        <UiRadioGroup
          v-model="form.auto_dns_management"
          :label="$t('domainManagement.popup.form.auto_dns_management.label')"
          :options="radioList"
          @change="changeDnsManagement"
        />
        <UiSelectElement
          v-if="form.auto_dns_management"
          v-model="form.dns_hosting_id"
          :placeholder="$t(`domainManagement.popup.form.dns_hosting_provider.placeholder`)"
          :label="$t(`domainManagement.popup.form.dns_hosting_provider.label`)"
          :list="dnsHostingProviders"
          filterable
          required
          :disabled="!form.auto_dns_management"
          :is-error="isInvalidField('dns_hosting_id')"
          size="big"
          @blur="checkField('dns_hosting_id')"
          @change="changeDnsId"
        />
        <template v-if="form.auto_dns_management && authenticationArrayFields.length">
          <UiSeparator />
          <template
            v-for="(authenticationFields, index) in authenticationArrayFields"
            :key="index"
          >
            <UiInput
              v-for="authenticationField in authenticationFields"
              :key="authenticationField"
              v-model="form.auth_data![authenticationField]"
              :placeholder="$t(`domainManagement.popup.form.${authenticationField}.placeholder`)"
              :label="$t(`domainManagement.popup.form.${authenticationField}.label`)"
              :required="activeGroup[index]"
              :disabled="!activeGroup[index]"
              autosize
              size="big"
              :is-error="isInvalidField(`auth_data.${authenticationField}`)"
              @blur="checkField(`auth_data.${authenticationField}`)"
            />
            <UiSeparator v-if="authenticationArrayFields.length - 1 !== index" />
          </template>
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading"
          @click="handleSave"
        >
          {{ $t(`domainManagement.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
